// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — mineriumManor.jsx   ·   THE HOUSE AT THE TOP OF THE YARD
// ────────────────────────────────────────────────────────────────────
// The outside of your Hall of Deeds, standing along the yard's north edge.
//
// ⚠ It used to be one 25-unit dark brick slab with an archway cut in it —
// in the yard's own screenshots it read as a black wall, not as a house,
// one door away from a boardwalk that had just become real architecture.
// ⚑ So it is built from the SAME kit and the SAME painted surfaces as the
// pier's shopfronts (clapboard, shingles, stone, lit windows), which is
// what makes the pier and the holding one world rather than two.
//
// MASSING, NOT CURVES: a two-storey centre block with a porch, and a
// lower wing either side.
//
// ⚑ THE PERMANENT ARCHWAY STILL EXISTS (L50): the porch door stands where
// `HOUSE_DOOR` has always been, and it is not a placeable. You cannot be
// locked out of your own holding.
// ════════════════════════════════════════════════════════════════════

import { useEffect, useMemo } from "react";
import * as THREE from "three";
import { Merger, boxUV, useFontGen } from "./pierTex";
import { clapTex, shingleTex, stoneTex, timberTex, windowTex, signTex, poolTex } from "./pierPaint";
import { Merged } from "./pierBuild";
import { Glows } from "./pierPieces";

const GOLD_HI = "#f0d060";

// A gable roof along x over a box [x0,x1] × [z0,z1] with its eave at y.
function gable(m, x0, x1, z0, z1, y, rise, over = 0.25) {
  const W = x1 - x0, D = z1 - z0, cx = (x0 + x1) / 2, cz = (z0 + z1) / 2;
  m.add("wall", new THREE.CylinderGeometry(1, 1, W, 3), [cx, y + 0.5 * (rise / 1.5), cz],
    [-Math.PI / 2, 0, Math.PI / 2], [D / 1.732, 1, rise / 1.5]);
  const a = Math.atan2(rise, D / 2);
  const L = Math.hypot(rise, D / 2) + over;
  m.box("roof", W + over * 2, 0.1, L, [cx, y + rise / 2 + 0.06, cz + D / 4], [a, 0, 0]);
  m.box("roof", W + over * 2, 0.1, L, [cx, y + rise / 2 + 0.06, cz - D / 4], [-a, 0, 0]);
  m.box("trim", W + over * 2 + 0.02, 0.1, 0.12, [cx, y + rise + 0.08, cz]);
}

export function Manor({ cx, front, title }) {
  const fontGen = useFontGen();   // the name board repaints when Cinzel lands
  const built = useMemo(() => {
    const m = new Merger();
    const wins = [];
    const win = (x, y, w, h) => {
      wins.push([x, y, w, h]);
      m.box("trim", w + 0.16, h + 0.16, 0.08, [x, y, front + 0.02]);
      m.box("trim", w + 0.3, 0.07, 0.18, [x, y - h / 2 - 0.1, front + 0.08]);
    };

    // ── the centre block: two storeys ──
    const C0 = cx - 4, C1 = cx + 4, CH = 4.6, CD = 5;
    m.box("stone", C1 - C0 + 0.2, 0.55, CD + 0.2, [cx, 0.275, front - CD / 2]);
    m.box("wall", C1 - C0, CH - 0.55, CD, [cx, 0.55 + (CH - 0.55) / 2, front - CD / 2]);
    m.box("trim", C1 - C0 + 0.12, 0.14, 0.18, [cx, 2.5, front + 0.05]);             // floor band
    for (const sx of [C0, C1]) m.box("trim", 0.16, CH, 0.16, [sx, CH / 2, front + 0.02]);
    gable(m, C0, C1, front - CD, front, CH, 1.7);
    m.box("stone", 0.5, 1.6, 0.5, [cx - 2.4, CH + 1.2, front - CD * 0.62]);
    m.box("stone", 0.5, 1.6, 0.5, [cx + 2.4, CH + 1.2, front - CD * 0.62]);
    win(cx - 2.6, 1.45, 1.0, 1.15); win(cx + 2.6, 1.45, 1.0, 1.15);
    win(cx - 2.6, 3.55, 0.9, 1.0); win(cx, 3.55, 0.9, 1.0); win(cx + 2.6, 3.55, 0.9, 1.0);

    // ── the two wings: one storey ──
    for (const s of [-1, 1]) {
      // ⚠ out to ±12.2 so the wings meet the side fences (at ±11 the yard's
      // back corners stood open onto the sea)
      const W0 = s < 0 ? cx - 12.2 : C1, W1 = s < 0 ? C0 : cx + 12.2, WH = 2.8, WD = 4;
      const wc = (W0 + W1) / 2;
      m.box("stone", W1 - W0 + 0.2, 0.45, WD + 0.2, [wc, 0.225, front - WD / 2 - 0.3]);
      m.box("wall", W1 - W0, WH - 0.45, WD, [wc, 0.45 + (WH - 0.45) / 2, front - WD / 2 - 0.3]);
      m.box("trim", 0.16, WH, 0.16, [s < 0 ? W0 : W1, WH / 2, front - 0.28]);
      gable(m, W0, W1, front - WD - 0.3, front - 0.3, WH, 1.1);
      for (let i = 0; i < 3; i++) {
        const x = W0 + ((W1 - W0) / 3) * (i + 0.5);
        wins.push([x, 1.45, 0.95, 1.05]);
        m.box("trim", 1.11, 1.21, 0.08, [x, 1.45, front - 0.28]);
        m.box("trim", 1.25, 0.07, 0.18, [x, 0.83, front - 0.22]);
      }
    }

    // ── the porch and the permanent door ──
    const P = 1.6;                                            // porch half-width
    m.box("timber", P * 2 + 0.6, 0.16, 1.25, [cx, 0.08, front + 0.55]);
    m.box("timber", P * 2 + 0.2, 0.08, 0.5, [cx, 0.04, front + 1.35]);        // the step
    for (const sx of [-1, 1]) {
      m.cyl("trim", 0.1, 0.12, 2.7, 12, [cx + sx * P, 1.45, front + 1.05]);
      m.box("trim", 0.3, 0.14, 0.3, [cx + sx * P, 2.85, front + 1.05]);
    }
    m.box("trim", P * 2 + 0.4, 0.2, 0.3, [cx, 2.98, front + 1.05]);
    gable(m, cx - P - 0.2, cx + P + 0.2, front - 0.05, front + 1.25, 3.08, 0.9, 0.15);
    m.box("dark", 1.35, 2.25, 0.1, [cx, 1.28, front + 0.02]);
    m.box("gold", 1.55, 0.12, 0.14, [cx, 2.46, front + 0.06]);
    for (const sx of [-1, 1]) m.box("gold", 0.1, 2.25, 0.12, [cx + sx * 0.72, 1.28, front + 0.05]);
    for (const sx of [-1, 1]) m.box("iron", 0.16, 0.24, 0.16, [cx + sx * 1.0, 1.95, front + 0.12]);

    const geos = m.build();
    ["wall", "roof", "stone", "timber"].forEach((k) => geos[k] && boxUV(geos[k], 0.85));
    return { geos, wins };
  }, [cx, front]);

  const mats = useMemo(() => ({
    wall: new THREE.MeshStandardMaterial({ map: clapTex(), color: "#6e5f82", roughness: 0.92 }),
    roof: new THREE.MeshStandardMaterial({ map: shingleTex(), color: "#9a8ab0", roughness: 0.95 }),
    stone: new THREE.MeshStandardMaterial({ map: stoneTex(), color: "#9a92a6", roughness: 1 }),
    timber: new THREE.MeshStandardMaterial({ map: timberTex(), color: "#8a6c4c", roughness: 1 }),
    trim: new THREE.MeshStandardMaterial({ color: "#e4d8bc", roughness: 0.7 }),
    gold: new THREE.MeshStandardMaterial({ color: "#c9a84c", emissive: "#6a4a10", emissiveIntensity: 0.7, metalness: 0.85, roughness: 0.3 }),
    iron: new THREE.MeshBasicMaterial({ color: "#ffd9a0", toneMapped: false }),
    dark: new THREE.MeshBasicMaterial({ color: "#07050c" }),
    glass: new THREE.MeshStandardMaterial({
      color: "#0c0810", emissive: "#ffcf8a", emissiveMap: windowTex(false),
      emissiveIntensity: 1.5, roughness: 0.3, toneMapped: false,
    }),
    doorGlow: new THREE.MeshBasicMaterial({
      map: poolTex(), color: GOLD_HI, transparent: true, depthWrite: false,
      blending: THREE.AdditiveBlending, toneMapped: false, opacity: 0.85,
    }),
    sign: new THREE.MeshBasicMaterial({ map: signTex(title || "Minerium", GOLD_HI, false), toneMapped: false }),
    spill: new THREE.MeshBasicMaterial({
      map: poolTex(), color: "#ffc98a", transparent: true, depthWrite: false,
      blending: THREE.AdditiveBlending, toneMapped: false, opacity: 0.32,
    }),
    porchPool: new THREE.MeshBasicMaterial({
      map: poolTex(), color: GOLD_HI, transparent: true, depthWrite: false,
      blending: THREE.AdditiveBlending, toneMapped: false, opacity: 0.45,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [title, fontGen]);

  useEffect(() => () => Object.values(built.geos).forEach((g) => g.dispose()), [built]);
  useEffect(() => () => Object.values(mats).forEach((m) => m.dispose()), [mats]);

  const glows = useMemo(() => [
    { x: cx - 1.0, y: 1.95, z: front + 0.2, color: "#ffc978", size: 1 },
    { x: cx + 1.0, y: 1.95, z: front + 0.2, color: "#ffc978", size: 1 },
    { x: cx, y: 1.2, z: front + 0.3, color: GOLD_HI, size: 1.2 },
  ], [cx, front]);

  return (
    <group>
      <Merged geos={built.geos} mats={mats} />
      {built.wins.map(([x, y, w, h], i) => (
        <mesh key={i} position={[x, y, (i < 5 ? front : front - 0.3) + 0.065]} material={mats.glass}>
          <planeGeometry args={[w, h]} />
        </mesh>
      ))}
      <mesh position={[cx, 1.28, front + 0.09]} material={mats.doorGlow}>
        <planeGeometry args={[1.7, 2.6]} />
      </mesh>
      {/* ⚑ LIGHT ON THE LAWN — painted, never lit. The boardwalk has warm
          pools under every lamp and doorway; the yard was a flat dark field
          in front of a house full of lit windows, which is a house nobody is
          home in. Each ground-floor window spills onto the grass, and the
          porch throws a pool out over the step. Zero lights. */}
      {built.wins.filter(([, y]) => y < 2).map(([x], i) => (
        <mesh key={`spill${i}`} position={[x, 0.02, front + 1.1]} rotation={[-Math.PI / 2, 0, 0]} material={mats.spill}>
          <planeGeometry args={[1.8, 2.2]} />
        </mesh>
      ))}
      <mesh position={[cx, 0.03, front + 2.4]} rotation={[-Math.PI / 2, 0, 0]} material={mats.porchPool}>
        <planeGeometry args={[5.2, 4.2]} />
      </mesh>
      {/* the holding's name, on a board under the porch gable — mounted, not floating */}
      <mesh position={[cx, 3.42, front + 1.28]} material={mats.sign}>
        <planeGeometry args={[2.9, 0.56]} />
      </mesh>
      <Glows points={glows} size={2} />
    </group>
  );
}
